import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Store } from "lucide-react";
import { inter, philosopher } from "@/lib/fonts";
import {
  IProduct,
  IProductVariantBulkPrice,
  IProductVariant,
  ICreateCartItem,
  getAvailableStock,
} from "muminsmart-utils";
import { toast } from "sonner";
import { useSession } from "@/contexts/AuthContext";
import { routes } from "@/lib/constants";
import {
  useAddCartItemMutation,
  useAddWishlistItemMutation,
  useGetProductReviewsQuery,
  useRemoveWishlistItemMutation,
} from "@/lib/api/apiSlice";
import { useCartWishlist } from "@/lib/hooks/useCartWishlist";
import { Link, useNavigate } from "react-router-dom";
import ResaleDialog from "./ResaleDialog";
import ProductReview, { ProductReviewProps } from "../cart/ProductReview";
import ProductImageCarousel from "./ProductImageCarousel";
import RelatedProducts from "./RelatedProducts";
import {
  getAvailableAttributeValues,
  getProductAttributes,
  getVariantSelectionMap,
  isAttributeValueAvailable,
  resolveVariantForAttributeChange,
} from "./utils/productVariantUtils";

interface ProductDetailsPreviewProps {
  product: IProduct;
  onSeePreview?: () => void;
  isPreview: boolean;
  defaultVariantId?: string;
}

function getBulkPriceId(item: IProductVariantBulkPrice) {
  return `${item.quantity}-${item.price}`;
}

export const ProductSectionTitle = ({ title }: { title: string }) => {
  return <h3 className={`text-2xl ${philosopher.className}`}>{title}</h3>;
};

const ProductDetails: FunctionComponent<ProductDetailsPreviewProps> = ({
  product,
  onSeePreview,
  isPreview,
  defaultVariantId,
}) => {
  const [selectedVariant, setSelectedVariant] = useState<IProductVariant>(
    product.variants[0],
  );

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [selectedAttributes, setSelectedAttributes] = useState<
    Record<string, string>
  >({});
  const [selectedBulkPriceId, setSelectedBulkPriceId] = useState<string | null>(
    null,
  );
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const { data: reviews = [] } = useGetProductReviewsQuery({
    productId: product.id,
  });
  const { status } = useSession();
  const navigate = useNavigate();
  const { isWishlisted, isInCart, getWishlistId } = useCartWishlist();
  const [addCartItem] = useAddCartItemMutation();
  const [addWishlistItem] = useAddWishlistItemMutation();
  const [removeWishlistItem] = useRemoveWishlistItemMutation();
  const description = product.description || "";
  const shouldShowReadMore = description.length > 200;
  const selectedVariantId = selectedVariant.id;

  const variantWishlisted = isWishlisted(selectedVariantId);
  const availableStock = useMemo(
    () => getAvailableStock(selectedVariant),
    [selectedVariant],
  );
  const shouldShowFooter = useMemo(
    () => availableStock > 0 || !variantWishlisted,
    [availableStock, variantWishlisted],
  );
  const variantInCart = isInCart(selectedVariantId);
  const selectedBulkPrice = useMemo(
    () =>
      selectedVariant.bulkPrices?.find(
        (item) => getBulkPriceId(item) === selectedBulkPriceId,
      ) || null,
    [selectedBulkPriceId, selectedVariant.bulkPrices],
  );

  const averageRating = useMemo(() => {
    if (!reviews.length) {
      return 0;
    }

    return (
      reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
    );
  }, [reviews]);

  const reviewSummary = useMemo<ProductReviewProps["reviewSummary"]>(
    () => ({
      rating: averageRating ? averageRating.toFixed(1) : "0.0",
      ratingsCount: `${reviews.length}`,
      reviewsCount: `${reviews.length}`,
    }),
    [averageRating, reviews.length],
  );

  useEffect(() => {
    if (!product.variants.length) {
      return;
    }

    const initialVariant =
      product.variants.find((variant) => variant.id === defaultVariantId) ||
      product.variants[0];

    setSelectedVariant(initialVariant);
    setSelectedAttributes(getVariantSelectionMap(initialVariant));
  }, [defaultVariantId, product]);

  useEffect(() => {
    setSelectedBulkPriceId(null);
  }, [selectedVariant]);

  const handleWishlistToggle = async () => {
    if (isWishlistLoading) {
      return;
    }

    if (status !== "authenticated") {
      toast.error("Please login to add items to wishlist");
      navigate(routes.auth.login);
      return;
    }

    setIsWishlistLoading(true);

    try {
      if (variantWishlisted) {
        const wishlistId = getWishlistId(selectedVariantId);

        if (!wishlistId) {
          toast.error("Failed to remove wishlist item");
          return;
        }

        await removeWishlistItem(wishlistId).unwrap();
        return;
      }

      await addWishlistItem({
        productId: product.id,
        productVariantId: selectedVariantId,
      }).unwrap();
    } catch {
      toast.error(
        variantWishlisted
          ? "Failed to remove wishlist item"
          : "Failed to add wishlist item",
      );
    } finally {
      setIsWishlistLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (isCartLoading) {
      return;
    }

    if (status !== "authenticated") {
      toast.error("Please login to add items to cart");
      navigate(routes.auth.login);
      return;
    }

    const quantity = selectedBulkPrice?.quantity ?? 1;

    if (quantity > availableStock) {
      toast.error("Selected quantity is not available in stock");
      return;
    }

    setIsCartLoading(true);

    try {
      const payload: ICreateCartItem = {
        productVariantId: selectedVariantId,
        quantity,
      };

      await addCartItem(payload).unwrap();
    } catch {
      toast.error("Failed to add item to cart");
    } finally {
      setIsCartLoading(false);
    }
  };

  const { attributes } = useMemo(() => getProductAttributes(product), [product]);

  const handleAttributeChange = (attributeId: string, value: string) => {
    const resolved = resolveVariantForAttributeChange(
      product,
      attributeId,
      value,
      selectedAttributes,
    );

    if (!resolved) {
      toast.error("Variant unavailable");
      return;
    }

    setSelectedAttributes(resolved.selection);
    setSelectedVariant(resolved.variant);
  };

  return (
    <div className="min-h-screen space-y-4">
      {isPreview ? (
        <section className="bg-primary">
          <p className="text-center text-white font-medium p-3">
            This is a preview of your product on store
          </p>
        </section>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 space-y-4">
        <section>
          <ProductImageCarousel
            product={product}
            selectedVariant={selectedVariant}
            handleWishlistToggle={handleWishlistToggle}
            isWishlisted={variantWishlisted}
            isWishlistLoading={isWishlistLoading}
            isPreview={isPreview}
            onSeePreview={onSeePreview}
            availableStock={availableStock}
            reviewSummary={reviewSummary}
          />
        </section>

        <div className="space-y-4">
          {/* Loop through attributes to select variants */}
          {attributes.map((attribute) => {
            const availableValues = getAvailableAttributeValues(
              product,
              attribute.id,
              selectedAttributes,
            );

            if (availableValues.length <= 1) {
              return null;
            }

            return (
              <section
                key={attribute.id}
                className="bg-white px-4 py-5 space-y-2"
              >
                <ProductSectionTitle title={`Select ${attribute.name}`} />
                  <div className="flex items-center gap-2 flex-wrap">
                    {availableValues.map((value) => {
                      const isSelected =
                        selectedAttributes[attribute.id] === value;
                      const isAvailable = isAttributeValueAvailable(
                        product,
                        attribute.id,
                        value,
                        selectedAttributes,
                      );

                      return (
                        <button
                          key={value}
                          type="button"
                          disabled={!isAvailable}
                          onClick={() =>
                            handleAttributeChange(attribute.id, value)
                          }
                          className={cn(
                            "items-center border rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                            {
                              "border-primary bg-primary text-white": isSelected,
                              "border-[#d9d9d9]": !isSelected,
                            },
                          )}
                        >
                          <span
                            className={cn("text-sm p-4", {
                              "text-white": isSelected,
                              "text-[#2f2a27]": !isSelected,
                            })}
                          >
                            {value}
                          </span>
                        </button>
                      );
                    })}
                  </div>
              </section>
            );
          })}

          <section className="bg-white px-4 py-5 space-y-2">
            {/* <p className="text-sm uppercase tracking-wide text-[#de8c36]">ASHARA DEALS</p> */}
            <ProductSectionTitle title={product.name} />

            {/* <h1
              className={`${philosopher.className} mt-1 text-2xl leading-tight text-[#2f2a27]`}
            >
              {product.name}
            </h1> */}

            <div className={`${inter.className} flex items-end gap-2`}>
              <span
                className={`${inter.className} text-xl font-bold text-[#111]`}
              >
                &#8377;{selectedVariant.discountedPrice}
              </span>
              <span className="pb-1 text-sm text-[#7d7d7d] line-through">
                &#8377;{selectedVariant.price}
              </span>
            </div>

            {/* <span className="mt-2 inline-flex rounded-full bg-[#ddefdb] px-3 py-1 text-sm text-[#3c9253]">
              Save 50% this order
            </span> */}

            <p className="flex items-center gap-2 text-sm text-[#6f6f6f]">
              <span className="rounded-full bg-[#2d9c73] px-2 py-0.5 text-white">
                {reviewSummary.rating} ★
              </span>
              {reviewSummary.reviewsCount} Reviews{" "}
              {product.orderCount > 0 ? (
                <>
                  |{" "}
                  <span className="text-primary font-bold">
                    {product.orderCount} units sold
                  </span>
                </>
              ) : null}
            </p>
          </section>

          {selectedVariant.bulkPrices?.length ? (
            <section className="bg-white px-4 py-5 space-y-2">
              <ProductSectionTitle title="Bulk Order" />

              <div className="mt-4 flex justify-start flex-wrap gap-2">
                {[...selectedVariant.bulkPrices]
                  .sort((a, b) => a.quantity - b.quantity)
                  .map((item: { quantity: number; price: number }) => (
                    <div
                      key={item.quantity + "-" + item.price}
                      className="text-center"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          const bulkPriceId = getBulkPriceId(item);
                          setSelectedBulkPriceId((prev) =>
                            prev === bulkPriceId ? null : bulkPriceId,
                          );
                        }}
                        aria-pressed={
                          selectedBulkPriceId === getBulkPriceId(item)
                        }
                        className={cn(
                          "rounded-md border py-1 px-3 text-sm transition-colors",
                          {
                            "border-primary bg-primary text-white":
                              selectedBulkPriceId === getBulkPriceId(item),
                            "border-primary text-[#2f2a27]":
                              selectedBulkPriceId !== getBulkPriceId(item),
                          },
                        )}
                      >
                        {item.quantity} pcs
                      </button>
                      <p className="mt-1 text-sm text-[#2f2a27]">
                        &#8377;{item.price}
                      </p>
                    </div>
                  ))}
              </div>
            </section>
          ) : null}

          <section className="bg-white px-4 py-5 space-y-2">
            <ProductSectionTitle title="Product Details" />

            <p className="mt-3 text-sm text-[#7c7c7c] whitespace-pre-line">
              {isDescriptionExpanded || !shouldShowReadMore
                ? description
                : `${description.substring(0, 200)}...`}
            </p>
            {shouldShowReadMore ? (
              <button
                type="button"
                className="mt-2 text-sm font-semibold text-[#c67c34]"
                onClick={() => setIsDescriptionExpanded((prev) => !prev)}
                aria-expanded={isDescriptionExpanded}
              >
                {isDescriptionExpanded ? "READ LESS" : "READ MORE"}
              </button>
            ) : null}
          </section>

          {product.seller ? (
            <section className="bg-white px-4 py-5 space-y-2">
              <ProductSectionTitle title="Seller Details" />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Store className="h-12 w-12 bg-[#e5f2f3] p-2 rounded-full" />
                  <p
                    className={`text-lg font-bold ${philosopher.className} line-clamp-1 text-[#2f2a27]`}
                  >
                    {product.seller.name}
                  </p>
                </div>
                <Link
                  to={routes.seller.id(product.seller.id)}
                  type="button"
                  className="rounded-full border border-primary p-2 text-sm text-primary tex-center"
                >
                  View Shop
                </Link>
              </div>
            </section>
          ) : null}

          {!isPreview && product.subCategories?.[0] ? (
            <RelatedProducts
              categoryId={product.subCategories[0].categoryId}
              subCategoryId={product.subCategories[0].id}
              excludeProductId={product.id}
            />
          ) : null}

          <ProductReview
            reviews={reviews}
            reviewSummary={reviewSummary}
            product={product}
          />
        </div>
      </div>

      {shouldShowFooter ? (
        <footer
          className={cn(
            "fixed bottom-0 z-10 flex w-full gap-3 border-t border-[#ebe7e2] bg-white px-4 py-4 rounded-t-3xl transition-transform duration-300 ease-out",
          )}
        >
          {availableStock > 0 ? (
            <>
              <ResaleDialog
                product={product}
                selectedVariant={selectedVariant}
              />
              <button
                type="button"
                className={cn(
                  "flex flex-1 items-center justify-center rounded-full bg-[#0d4e5b] py-3 text-base text-white",
                  isCartLoading && "pointer-events-none opacity-60",
                )}
                onClick={() => {
                  if (variantInCart) {
                    navigate(routes.cart);
                    return;
                  }

                  void handleAddToCart();
                }}
              >
                {variantInCart
                  ? "View Cart"
                  : isCartLoading
                    ? "Adding..."
                    : "Add To Cart"}
              </button>
            </>
          ) : !variantWishlisted ? (
            <button
              type="button"
              className="flex flex-1 items-center justify-center rounded-full bg-[#0d4e5b] py-3 text-base text-white"
              onClick={() => {
                void handleWishlistToggle();
              }}
            >
              Add To Wishlist
            </button>
          ) : null}
        </footer>
      ) : null}
    </div>
  );
};

export default ProductDetails;
